// Error handling utilities for API calls
import { error } from '@/utils/logger';

export class ApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

export const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    const { status, data } = error.response;
    const messages = data?.messages;
    const fromMessages =
      Array.isArray(messages) && messages.length > 0
        ? messages.join(' ')
        : null;
    const message =
      fromMessages ||
      data?.message ||
      data?.error ||
      `HTTP Error ${status}`;
    throw new ApiError(message, status, data);
  } else if (error.request) {
    // Request made but no response received
    throw new ApiError("Network error: No response from server", 0);
  } else {
    // Something else happened
    throw new ApiError(error.message || "Unknown error occurred", 0);
  }
};

export const withErrorHandling = (apiCall) => {
  return async (...args) => {
    try {
      return await apiCall(...args);
    } catch (error) {
      handleApiError(error);
    }
  };
};

// Toast notification utility (you can integrate with your preferred toast library)
export const showApiError = (
  err,
  defaultMessage = "Something went wrong"
) => {
  const message = err instanceof ApiError ? err.message : defaultMessage;

  // Replace with your toast notification implementation
  error("API Error:", message);

  // Example with a simple toast implementation:
  // toast.error(message);

  return message;
};

/**
 * Check if error is a server error (5xx status code)
 * @param {any} err - Error object (typically AxiosError)
 * @returns {boolean} - True if error is a server error
 */
export const isServerError = (err) => {
  return err?.response?.status >= 500 || err?.response?.status === 500;
};

/**
 * Retry utility for failed API calls with exponential backoff
 * Automatically skips retries for server errors (5xx) - server is down
 * 
 * @param {Function} apiCall - Function that returns a Promise
 * @param {Object} [options] - Retry options (all optional)
 * @param {number} [options.maxRetries=2] - Maximum number of retry attempts
 * @param {number} [options.initialDelay=500] - Initial delay in milliseconds
 * @param {Function} [options.shouldRetry] - Optional function to determine if error should be retried (default: skips 5xx errors)
 * @returns {Promise<any>} - Result of the API call
 */
export const retryWithBackoff = async (
  apiCall,
  options = {}
) => {
  const {
    maxRetries = 2,
    initialDelay = 500,
    shouldRetry = (error) => !isServerError(error), // Don't retry server errors by default
  } = options || {};

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await apiCall();
    } catch (error) {
      // Don't retry on server errors (5xx) - server is down
      if (!shouldRetry(error)) {
        throw error;
      }

      // If this is the last attempt, throw the error
      if (attempt === maxRetries - 1) {
        throw error;
      }

      // Exponential backoff: initialDelay * 2^attempt
      // e.g., 500ms, 1000ms, 2000ms
      const delay = initialDelay * Math.pow(2, attempt);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
};

// Legacy retry utility for backward compatibility
// Retry utility for failed API calls
export const retryApiCall = async (apiCall, maxRetries = 3, delay = 1000) => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await apiCall();
    } catch (error) {
      if (attempt === maxRetries - 1) {
        throw error;
      }

      // Wait before retrying
      await new Promise((resolve) =>
        setTimeout(resolve, delay * Math.pow(2, attempt))
      );
    }
  }
};
